import { HttpClient } from '@angular/common/http';
import { IBusinessConfiguration, IBusinessConfigurationResponse } from '../models/business-configuration.model';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { Country, IAccountInformation, IBillingAddress, IBroadcastResponseAPI, IContents, IFile, IFileSizeLimitMap, IPaymentHistoryItem, IPermission, IResponseMessage, IScheduleConflictData, ISubscription, ISubscriptionResponseAPI, IUser, IUserModulePermission, IUserReponseAPI, State } from '../models';
import { APIConstants } from '../common/api-constants';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import type { DialogData } from '../shared/zignflix-dialog/zignflix-dialog.component';
import type { ScheduleConflictDialogDataDialogData } from '../shared/zignflix-schedule-conflict-dialog/zignflix-schedule-conflict-dialog.component';
import type { CompositionValidationDialogData } from '../shared/composition-validation-confirm/composition-validation-confirm.component';
import type { ScreenValidationDialogData } from '../shared/screen-validation-confirm/screen-validation-confirm.component';

interface ListCacheEntry<T> {
  value: T;
  signature: string;
}

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private _isLoading = false;
  private readonly loadingStateSubject = new BehaviorSubject<boolean>(false);
  readonly loadingState$ = this.loadingStateSubject.asObservable();

  get isLoading(): boolean {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    const normalizedValue = Boolean(value);
    if (this._isLoading === normalizedValue) return;

    this._isLoading = normalizedValue;
    this.loadingStateSubject.next(normalizedValue);
  }

  readonly listSkeletonDelayMs = 250;
  private knownEmptyLists = new Set<string>();
  private listCache = new Map<string, ListCacheEntry<unknown>>();

  profileDetail: IUser = {} as IUser;
  businessConfig: IBusinessConfiguration = { config_data: { launcher_menu_limit: 10 } };

  get launcherMenuLimit(): number {
    const limit = this.businessConfig?.config_data?.launcher_menu_limit;
    return Number.isInteger(limit) && Number(limit) >= 5 && Number(limit) <= 15 ? Number(limit) : 10;
  }

  loadBusinessConfiguration(): Observable<IBusinessConfigurationResponse> {
    return this.http.get<IBusinessConfigurationResponse>(APIConstants.businessConfiguration).pipe(
      tap(response => { this.businessConfig = response.data; })
    );
  }

  updateBusinessConfiguration(configData: IBusinessConfiguration['config_data']): Observable<IBusinessConfigurationResponse> {
    return this.http.patch<IBusinessConfigurationResponse>(APIConstants.businessConfiguration, { config_data: configData }).pipe(
      tap(response => { this.businessConfig = response.data; })
    );
  }
  currentUserProfile$ = new BehaviorSubject<IUser | null>(null);
  subscriptionDetail: ISubscription = {} as ISubscription;

  userPermission: IPermission[] = [];

  isPermissionLoaded = false;

  selectedFolder: IFile | null = null;

  filesToUpload: IContents[] = [];

  maxFileSizes: IFileSizeLimitMap = {} as IFileSizeLimitMap;

  startUploadingFile = new Subject<IContents>();
  refreshContents = new Subject<void>();
  refreshSubscription = new Subject<void>();
  openChangePasswordSideNav = new Subject<void>();
  openUpgradePlanPopup = new Subject<void>();
  openSubscriptionExpiredPopup = new Subject<void>();
  openEditProfiledSideNav = new Subject<void>();
  refreshProfileDetails = new Subject<void>();
  newContentAdded = new Subject<IContents>();

  private activeDialogRef: MatDialogRef<unknown> | null = null;
  private activeDialogPromise: Promise<boolean> | null = null;
  private compositionDialogRef: MatDialogRef<unknown> | null = null;
  private screenDialogRef: MatDialogRef<unknown> | null = null;

  constructor(private http: HttpClient, private dialog: MatDialog) { }

  isListKnownEmpty(listKey: string): boolean {
    return this.knownEmptyLists.has(this.getUserScopedListKey(listKey));
  }

  updateKnownEmptyList(listKey: string, isEmpty: boolean): void {
    const scopedListKey = this.getUserScopedListKey(listKey);

    if (isEmpty) {
      this.knownEmptyLists.add(scopedListKey);
      return;
    }

    this.knownEmptyLists.delete(scopedListKey);
  }

  hasListCache(listKey: string): boolean {
    return this.listCache.has(this.getUserScopedListKey(listKey));
  }

  getListCache<T>(listKey: string): T | null {
    return (this.listCache.get(this.getUserScopedListKey(listKey))?.value as T) || null;
  }

  setListCacheIfChanged<T>(listKey: string, value: T): boolean {
    const scopedListKey = this.getUserScopedListKey(listKey);
    const signature = this.createCacheSignature(value);
    const currentEntry = this.listCache.get(scopedListKey);

    if (currentEntry?.signature === signature) {
      return false;
    }

    this.listCache.set(scopedListKey, { value, signature });
    return true;
  }

  private getUserScopedListKey(listKey: string): string {
    const userKey = this.currentUserProfile$.value?.email || this.profileDetail?.email || 'anonymous';
    return `${userKey}:${listKey}`;
  }

  private createCacheSignature(value: unknown): string {
    try {
      return JSON.stringify(value);
    } catch {
      return `${Date.now()}`;
    }
  }

  getProfileDetails(): Observable<IUserReponseAPI> {
    return this.http.get<IUserReponseAPI>(APIConstants.profile).pipe(
      tap((res) => {
        if (res && res.data) {
          this.profileDetail = res.data;
          this.currentUserProfile$.next(res.data);
        }
      })
    );
  }

  isLauncherAvailable(): boolean {
    const profile = this.currentUserProfile$.value || this.profileDetail;
    return profile?.is_launcher_available === true;
  }

  updateProfileDetails(user: FormData): Observable<IResponseMessage> {
    return this.http.put<IResponseMessage>(APIConstants.profile, user);
  }

  getSubscriptionDetails(): Observable<ISubscriptionResponseAPI> {
    return this.http.get<ISubscriptionResponseAPI>(APIConstants.subscription);
  }

  getCurrentUserPermission(): Observable<IUserModulePermission> {
    return this.http.get<IUserModulePermission>(APIConstants.userModulePermissions);
  }

  getCurrentSchedules(): Observable<IBroadcastResponseAPI> {
    return this.http.get<IBroadcastResponseAPI>(APIConstants.currentSchedules);
  }

  getAccountInformation(): Observable<IAccountInformation> {
    return this.http.get<IAccountInformation>(APIConstants.accountInfo);
  }

  openDialog(data: DialogData): Promise<boolean> {
    // Set the promise before the lazy import resolves so simultaneous requests
    // all reuse the same dialog instead of opening one dialog per request.
    if (this.activeDialogPromise) {
      return this.activeDialogPromise;
    }

    if (this.activeDialogRef) {
      return new Promise<boolean>((resolve) => {
        this.activeDialogRef?.afterClosed().subscribe((result) => resolve(Boolean(result)));
      });
    }

    const dialogPromise = this.createDialog(data);
    this.activeDialogPromise = dialogPromise;

    const clearDialogPromise = () => {
      if (this.activeDialogPromise === dialogPromise) {
        this.activeDialogPromise = null;
      }
    };

    void dialogPromise.then(clearDialogPromise, clearDialogPromise);

    return dialogPromise;
  }

  private async createDialog(data: DialogData): Promise<boolean> {
    const { ZignflixDialogComponent } = await import('../shared/zignflix-dialog/zignflix-dialog.component');
    const dialogRef = this.dialog.open(ZignflixDialogComponent, {
      id: 'zignflix-dialog',
      width: '440px',
      maxWidth: 'calc(100vw - 32px)',
      data,
      disableClose: true,
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      ariaLabelledBy: 'zignflix-dialog-title',
      panelClass: 'zignflix-dialog-panel',
      backdropClass: 'zignflix-dialog-backdrop'
    });

    this.activeDialogRef = dialogRef;

    return new Promise<boolean>((resolve) => {
      dialogRef.afterClosed().subscribe((result) => {
        if (this.activeDialogRef === dialogRef) {
          this.activeDialogRef = null;
        }
        resolve(Boolean(result));
      });
    });
  }

  async openScheduleConflictDialog(data: ScheduleConflictDialogDataDialogData): Promise<IScheduleConflictData[]> {
    // Return the existing dialog's result if one is already active
    if (this.activeDialogRef) {
      return this.activeDialogRef.afterClosed().toPromise();
    }

    const { ZignflixScheduleConflictDialogComponent } = await import('../shared/zignflix-schedule-conflict-dialog/zignflix-schedule-conflict-dialog.component');
    this.activeDialogRef = this.dialog.open(ZignflixScheduleConflictDialogComponent, {
      width: '620px',
      maxWidth: '95vw',
      data: data
    });

    // Reset reference when the dialog is closed
    this.activeDialogRef.afterClosed().subscribe(() => {
      this.activeDialogRef = null;
    });

    return this.activeDialogRef.afterClosed().toPromise();
  }

  async openCompositionValidationDialog(data: CompositionValidationDialogData): Promise<boolean> {
    // Return the existing dialog's result if one is already active
    if (this.compositionDialogRef) {
      return this.compositionDialogRef.afterClosed().toPromise();
    }

    const { CompositionValidationConfirmComponent } = await import('../shared/composition-validation-confirm/composition-validation-confirm.component');
    this.compositionDialogRef = this.dialog.open(CompositionValidationConfirmComponent, {
      width: '700px',
      data: data
    });

    // Reset reference when the dialog is closed
    this.compositionDialogRef.afterClosed().subscribe(() => {
      this.compositionDialogRef = null;
    });

    return this.compositionDialogRef.afterClosed().toPromise();
  }

  async openScreenValidationDialog(data: ScreenValidationDialogData): Promise<boolean> {
    // Return the existing dialog's result if one is already active
    if (this.screenDialogRef) {
      return this.screenDialogRef.afterClosed().toPromise();
    }

    const { ScreenValidationConfirmComponent } = await import('../shared/screen-validation-confirm/screen-validation-confirm.component');
    this.screenDialogRef = this.dialog.open(ScreenValidationConfirmComponent, {
      width: '700px',
      data: data
    });

    // Reset reference when the dialog is closed
    this.screenDialogRef.afterClosed().subscribe(() => {
      this.screenDialogRef = null;
    });

    return this.screenDialogRef.afterClosed().toPromise();
  }

  downloadInvoice(element: IPaymentHistoryItem) {
    const paymentId = element.payment_id;
    const fullInvoiceNumber = element.invoice_number;
    // const invoiceNumber = fullInvoiceNumber?.split('#')[1];

    return this.http.get(APIConstants.invoiceReport + '?payment_id=' + paymentId + '&invoice_number=' + fullInvoiceNumber, {
      responseType: 'blob'  // Important to get binary data
    });
  }

  getBillingInfo(): Observable<IBillingAddress[]> {
    return this.http.get<IBillingAddress[]>(APIConstants.billingAddress);
  }

  addBillingInfo(billing_info: IBillingAddress): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.billingAddress, billing_info);
  }

  updateBillingInfo(id: number, billing_info: IBillingAddress): Observable<IResponseMessage> {
    return this.http.put<IResponseMessage>(APIConstants.billingAddress + id + '/', billing_info);
  }

  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${APIConstants.countryStateApi}`);
  }

  getStates(countryId: string): Observable<State[]> {
    return this.http.get<State[]>(`${APIConstants.countryStateApi}?country_id=${countryId}`);
  }

  getMaxFileSizes(): Observable<IFileSizeLimitMap> {
    return this.http.get<IFileSizeLimitMap>(APIConstants.maxFileSizes);
  }
}
